import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  Badge,
  Button,
  Card,
  DetailLayout,
  Skeleton,
} from "@rumbledirect/ui-v2";
import { ContractExpirationActionsCard } from "@/contractExpirations/ContractExpirationActionsCard";
import { ContractExpirationAskDialog } from "@/contractExpirations/ContractExpirationAskDialog";
import { ContractExpirationDecisionDialog } from "@/contractExpirations/ContractExpirationDecisionDialog";
import { ContractExpirationHandlingCard } from "@/contractExpirations/ContractExpirationHandlingCard";
import { getExpirationVehicleTitle } from "@/contractExpirations/contractExpirationFields";
import {
  CONTRACT_EXPIRATION_STATUS_LABELS,
  CONTRACT_EXPIRATION_STATUS_VARIANTS,
} from "@/contractExpirations/contractExpirationLabels";
import {
  ContractExpirationArrangedSection,
  ContractExpirationContractSection,
} from "@/contractExpirations/ContractExpirationSections";
import { useContractExpirationActions } from "@/contractExpirations/useContractExpirationActions";
import { ContractExpirationFollowUpCard } from "@/contractExpirations/ContractExpirationFollowUpCard";
import { useGetAdminContractExpirationsId } from "@/lib/api/generated/contract-expiration/contract-expiration";

export const Route = createFileRoute(
  "/_authenticated/contract-expirations_/$contractExpirationId",
)({
  component: ContractExpirationDetailPage,
});

/**
 * The work list as it opens by itself.
 *
 * The link back cannot carry the filters the user had set — those live in the
 * history entry the browser's back button restores — so it names the default
 * reading rather than a half-filled one.
 */
const WORK_LIST_SEARCH = {
  page: 1,
  status: "open",
  decision: undefined,
  endsFrom: undefined,
  endsUntil: undefined,
  site: undefined,
  leaseCompany: undefined,
} as const;

/**
 * One expiration in full, and the two acts that move it along.
 *
 * The acts sit here rather than on the row they used to hang off: deciding what
 * happens to a car is a decision about that car, and the work list has no room
 * to say what it is being decided about.
 */
function ContractExpirationDetailPage(): ReactNode {
  const { contractExpirationId } = Route.useParams();

  const {
    data: expiration,
    isPending,
    isError,
    refetch,
  } = useGetAdminContractExpirationsId(contractExpirationId);

  const [asking, setAsking] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const actions = useContractExpirationActions();

  return (
    <div className="space-y-5">
      {/*
        Back to the work list on its default reading; the filters the user had
        set come back with the browser's own history entry.
      */}
      <Link
        to="/contract-expirations"
        search={WORK_LIST_SEARCH}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Terug naar contractexpiraties
      </Link>

      {isError ? (
        <Card
          role="alert"
          className="flex flex-col items-center gap-3 py-10 text-center"
        >
          <p className="text-muted-foreground">
            We konden deze contractexpiratie niet laden.
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Opnieuw proberen
          </Button>
        </Card>
      ) : isPending ? (
        <>
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-64 w-full" />
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              {getExpirationVehicleTitle(expiration)}
            </h1>
            <Badge
              variant={
                CONTRACT_EXPIRATION_STATUS_VARIANTS[expiration.expirationStatus]
              }
              size="sm"
            >
              {CONTRACT_EXPIRATION_STATUS_LABELS[expiration.expirationStatus]}
            </Badge>
          </div>

          {/*
            Every button on this page sits in the rail, in the same place as
            on the request detail. On a phone it leads: what can be done is
            why the page was opened, and the facts are there to check it
            against.
          */}
          <DetailLayout
            railFirst
            rail={
              <ContractExpirationActionsCard
                expiration={expiration}
                actions={{
                  isSubmitting: actions.isAsking || actions.isDeciding,
                  onAsk: () => setAsking(true),
                  onDecide: () => setDeciding(true),
                }}
              >
                <ContractExpirationFollowUpCard expiration={expiration} />
              </ContractExpirationActionsCard>
            }
          >
            {/*
              The handling leads: the page is opened to move the expiration
              on, and the sections below are what the choice is checked
              against.
            */}
            <ContractExpirationHandlingCard expiration={expiration} />
            <ContractExpirationContractSection expiration={expiration} />
            <ContractExpirationArrangedSection expiration={expiration} />
          </DetailLayout>

          <ContractExpirationAskDialog
            expiration={asking ? expiration : undefined}
            onOpenChange={(open) => setAsking(open)}
            isSubmitting={actions.isAsking}
            onConfirm={() =>
              actions.ask(contractExpirationId, {
                onSuccess: () => setAsking(false),
              })
            }
          />

          <ContractExpirationDecisionDialog
            expiration={deciding ? expiration : undefined}
            onOpenChange={(open) => setDeciding(open)}
            isSubmitting={actions.isDeciding}
            onSubmit={(input) =>
              actions.decide(contractExpirationId, input, {
                onSuccess: () => setDeciding(false),
              })
            }
          />
        </>
      )}
    </div>
  );
}
